// PDP Buy Gate — VISUAL layer ("your options are loading").
//
// Companion to assets/pdp-buy-gate-arm.js, which owns the FUNCTIONAL block (intercepting
// premature taps during the MageWorx APO dead window) + the add_to_cart_blocked signal.
// This file owns only the honest loading treatment: a skeleton above the buy buttons, a
// not-ready dim, and a polite a11y announcement — so the buy area reads as "loading", not
// "ready but dead". It tears itself down the instant APO's validated controls render, or
// at a 12s fail-open ceiling.
//
// SCOPE: detection is SERVER-SIDE. snippets/buy-buttons.liquid sets data-md-apo-gate="true"
// on <product-form> only for APO products (product.has_only_default_variant + door). Plain
// products and native multi-variant products carry no marker → this never engages, exactly
// as before. The previous version bailed on a `product-form .product-form` selector that
// never matched (the inner form is `form.form`) — fixed here to target the custom element.
(function () {
  'use strict';

  var GATE_SELECTOR = 'product-form[data-md-apo-gate="true"]';

  // APO has finished hydrating once its post-init validated controls / option swatches
  // exist. Any one of these proves the real options are interactive.
  var APO_READY_SELECTOR =
    '.mw-apo-buy-it-now-cloned, .mw-apo-add-to-cart-cloned, .mw-apo-container .mw-option';

  // The server renders the APO native button as a "Loading options…" placeholder, with the
  // real label in data-ready-label (snippets/buy-buttons.liquid). Nothing else ever swaps it
  // back, so once options are ready we do it here — otherwise the button (and the sticky-bar
  // proxy that mirrors its text) reads "Loading options…" for the rest of the page's life,
  // including to screen readers.
  function restoreReadyLabel(atc) {
    if (!atc) return;
    atc.classList.remove('pdp-buy-gate__cta--loading');
    var label = atc.getAttribute('data-ready-label');
    var span = atc.querySelector('span');
    if (label && span && /loading options/i.test(span.textContent)) {
      span.textContent = label;
    }
  }

  function init() {
    // The <product-form> custom element itself carries class "product-form"; the inner
    // <form> is `form.form`. Target the element directly (the old selector matched nothing).
    var form = document.querySelector(GATE_SELECTOR);
    if (!form) return; // not a gated APO product → leave everything exactly as-is

    // Already hydrated before we ran (fast connection / cache)? Nothing to gate, but the
    // server-rendered placeholder label still has to be replaced.
    if (document.querySelector(APO_READY_SELECTOR)) {
      restoreReadyLabel(form.querySelector('.product-form__submit'));
      return;
    }

    var buttons = form.querySelector('.product-form__buttons');
    if (!buttons) return;

    var atc = form.querySelector('.product-form__submit');
    var gateActive = true;

    // --- Visual state -------------------------------------------------------
    form.classList.add('is-options-loading');

    // Skeleton placeholder inserted directly above the buy buttons. createElement only
    // (no innerHTML) so the theme's XSS/CI gate stays green.
    var skeleton = document.createElement('div');
    skeleton.className = 'pdp-buy-gate__skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    [
      'pdp-buy-gate__bar',
      'pdp-buy-gate__bar',
      'pdp-buy-gate__bar pdp-buy-gate__bar--cta',
    ].forEach(function (cls) {
      var bar = document.createElement('span');
      bar.className = cls;
      skeleton.appendChild(bar);
    });
    buttons.parentNode.insertBefore(skeleton, buttons);

    // Polite live-region so SR / keyboard users know options are loading and, on ready,
    // that they can proceed. It MUST live OUTSIDE the aria-hidden skeleton (a live region
    // nested in aria-hidden is pruned from the a11y tree), so insert it as a sibling.
    var announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'pdp-buy-gate__sr';
    announcer.id = 'pdp-buy-gate-status';
    buttons.parentNode.insertBefore(announcer, skeleton);
    // Point the buy button at the status region so a screen-reader user who focuses it
    // during the dead window hears "Add to Cart … Loading product options" instead of a
    // button that silently does nothing. aria-describedby (not aria-disabled) because APO
    // reads the native control's own attributes during hydration. Removed on teardown.
    if (atc) atc.setAttribute('aria-describedby', 'pdp-buy-gate-status');
    requestAnimationFrame(function () {
      if (gateActive) announcer.textContent = 'Loading product options…';
    });

    // --- Blocked-tap nudge --------------------------------------------------
    // The arm script dispatches pdp-buy-gate:blocked when it swallows a premature tap.
    // Pulse the skeleton + ring the actual control the finger landed on (outline-only,
    // no CLS) so a blocked tap never reads as a dead button.
    var hintTimer = null;
    function flashHint(hit) {
      skeleton.classList.add('pdp-buy-gate__skeleton--nudge');
      announcer.textContent = 'One moment, your options are still loading.';
      if (hit && hit.classList && form.contains(hit)) {
        hit.classList.add('pdp-buy-gate__btn--blocked');
        setTimeout(function () {
          hit.classList.remove('pdp-buy-gate__btn--blocked');
        }, 500);
      }
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = setTimeout(function () {
        skeleton.classList.remove('pdp-buy-gate__skeleton--nudge');
      }, 1200);
    }
    function onBlocked(e) {
      if (!gateActive) return;
      flashHint(e && e.detail ? e.detail.target : null);
    }
    document.addEventListener('pdp-buy-gate:blocked', onBlocked);

    // --- Teardown / state ---------------------------------------------------
    var observer = null;
    var timeoutMsg = null;

    // Tear the loading treatment down — ONLY when APO's validated options are ready.
    // Fail CLOSED: there is no time-based teardown. The loading state persists until the
    // real options load (or the shopper refreshes), so the button never reverts to a live
    // "Add to cart" over an un-configured product.
    function openGate() {
      if (!gateActive) return;
      gateActive = false;
      document.removeEventListener('pdp-buy-gate:blocked', onBlocked);
      document.removeEventListener('pdp-buy-gate:open', onArmOpen);
      document.removeEventListener('pdp-buy-gate:timeout', onArmTimeout);
      if (observer) observer.disconnect();
      if (hintTimer) clearTimeout(hintTimer);
      if (atc) {
        atc.removeAttribute('aria-describedby');
        atc.removeAttribute('aria-busy');
        restoreReadyLabel(atc);
      }
      if (timeoutMsg && timeoutMsg.parentNode) {
        timeoutMsg.parentNode.removeChild(timeoutMsg);
      }
      form.classList.remove('is-options-loading');
      announcer.textContent = 'Product options ready.';
      skeleton.classList.add('pdp-buy-gate__skeleton--done');
      setTimeout(function () {
        if (skeleton.parentNode) skeleton.parentNode.removeChild(skeleton);
      }, 250);
      setTimeout(function () {
        if (announcer.parentNode) announcer.parentNode.removeChild(announcer);
      }, 1000);
    }

    // Arm released (APO's validated options rendered). Guard against a spoofed event:
    // only follow once the arm's functional gate has actually released.
    function onArmOpen() {
      if (window.__pdpBuyGate && window.__pdpBuyGate.isActive()) return;
      openGate();
    }

    // Arm hit its fail-CLOSED ceiling: options still aren't ready and the buy button stays
    // blocked. Swap the shimmer skeleton for an honest, visible message — do NOT tear the
    // gate down; a late APO render still opens it via onArmOpen or the observer below.
    function onArmTimeout() {
      if (!gateActive || timeoutMsg) return;
      skeleton.classList.remove('pdp-buy-gate__skeleton--nudge');
      // Single message string for both the visible <p> and the AT announcement so they
      // never drift. The <p> is plain text (NO role) — the pre-existing polite `announcer`
      // is the sole live region, avoiding a double announcement.
      var msg =
        'Product options are taking longer than usual to load. Please refresh the page to continue.';
      timeoutMsg = document.createElement('p');
      timeoutMsg.className = 'pdp-buy-gate__timeout';
      timeoutMsg.textContent = msg;
      if (skeleton.parentNode) skeleton.parentNode.insertBefore(timeoutMsg, skeleton);
      announcer.textContent = msg;
    }

    document.addEventListener('pdp-buy-gate:open', onArmOpen);
    document.addEventListener('pdp-buy-gate:timeout', onArmTimeout);

    // Backstop: tear down the instant APO's validated controls appear, even if the arm
    // script's open event is missed (e.g. its asset failed to load). No timer — fail
    // CLOSED: the loading state persists until options truly render.
    if (document.querySelector(APO_READY_SELECTOR)) {
      openGate();
    } else {
      observer = new MutationObserver(function () {
        if (document.querySelector(APO_READY_SELECTOR)) openGate();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
